// react
import { useCallback, useMemo, useState } from 'react';

// store
import { useUpdateMemberMutation } from 'store/api/careCircle';
import { useGenerateActionPlanMutation } from 'store/api/actionPlan';
import { useGetAllPersonalizationQuery } from 'store/api/personalization';
import { useGetAllCareStageQuery } from 'store/api/careStage';
import {
  useUpdateCareCircleNamesMutation,
  useUpdateIsOnboardedMutation,
} from 'store/api/iam';

// models
import {
  CareCircleMember,
  CareProfileCategoryID,
  FieldType,
} from '@karehero/models';

// hooks
import { useCareCircle, useCareProfile, useCareProfileForm } from 'hooks';

// components
import { OnboardingActivatedStart } from '@karehero/llama/components/organisms/OnboardingActivatedStart/OnboardingActivatedStart';
import { OnboardingEditor } from '@karehero/llama/components/organisms/OnboardingEditor/OnboardingEditor';
import { OnboardingLoading } from '@karehero/llama/components/organisms/OnboardingLoading/OnboardingLoading';
import { OnboardingTOS } from '@karehero/llama/components/organisms/OnboardingTOS/OnboardingTOS';
import { OnboardingTypeform } from '@karehero/llama/components/organisms/OnboardingTypeform/OnboardingTypeform';

import { OnboardingActionType } from '@karehero/llama/components/pages/OnboardingFlow/OnboardingFlow';

// config
import { onboardingLoadingConfig } from 'config/onboarding';
import { useSelector } from 'store';
import { getAccount } from 'store/slices/iamSlice';

// capacitor
import { Capacitor } from '@capacitor/core';

const validateNotEmpty = (value?: string) =>
  (value && value.length > 0) ||
  (typeof value === 'boolean' && value !== undefined);

const validatePersonalization = (value: any) => {
  if (!value) return false;
  const parsedValue = JSON.parse(value || '[]');
  return parsedValue.length > 0;
  /* return (
    parsedValue.length > 0 &&
    parsedValue.filter((p: string) =>
      ['find-or-manage-care', 'help-with-funding-care'].includes(p),
    ).length > 0
  ); */
};

const validateCareStage = (value: any) => !!value;

interface UseOnboardingOptions {
  typeformID?: string;
}

export const useOnboardingV1 = (options?: UseOnboardingOptions) => {
  // hooks
  const [updateCareCircleNames] = useUpdateCareCircleNamesMutation();
  const [updateMember] = useUpdateMemberMutation();
  const [generateActionPlan] = useGenerateActionPlanMutation();
  const { data: personalizationOptions } = useGetAllPersonalizationQuery();
  const { data: careStageOptions } = useGetAllCareStageQuery();
  const { careCircleMember } = useCareCircle();
  const account = useSelector(getAccount);
  const [updateIsOnboarded] = useUpdateIsOnboardedMutation();

  // states
  const [careRecipientFirstName] = useState<{
    value: string;
  }>();
  const [personalization, setPersonalization] = useState<{ value: string }>({
    value: '[]',
  });
  const [careStage, setCareStage] = useState<{ value: string }>({
    value: '',
  });

  // memos
  const parsedPersonalization = useMemo(
    () => JSON.parse(personalization?.value || '[]'),
    [personalization.value],
  );
  const careProfileOptions = useMemo(
    () => ({
      categoryID: CareProfileCategoryID.Onboarding,
      isSkipAnswers: true,
    }),
    [],
  );

  const { createManyAnswer, forms, answers, setAnswers } =
    useCareProfile(careProfileOptions);

  const {
    createManyAnswer: createManyAnswerConsent,
    form: formConsent,
    answers: answersConsent,
    setAnswers: setAnswersConsent,
  } = useCareProfileForm({
    categoryID: CareProfileCategoryID.Consent,
    formID: account?.accountConsents?.[0]?.consentFormId,
    isSkipAnswers: true,
  });

  // methods
  // update care circle names
  const handleUpdateCareCircleNames =
    useCallback(async (): Promise<boolean> => {
      const res = await updateCareCircleNames({
        careRecipientFirstName: careRecipientFirstName?.value || '',
      });
      if (!('data' in res)) {
        console.error('Failed to update account', res.error);
        return false;
      }

      return true;
    }, [careRecipientFirstName, updateCareCircleNames]);

  // care stage
  const handleUpdateCareStage = useCallback(async (): Promise<boolean> => {
    const res = await updateMember({
      ...(careCircleMember as CareCircleMember),
      careCircleID: careCircleMember?.careCircleID || '',
      careStageID: careStage.value,
    });
    if (!('data' in res)) {
      console.error('Failed to update care stage', res.error);
      return false;
    }

    return true;
  }, [careStage, careCircleMember, updateMember]);

  // personalization
  const handleUpdatePersonalization =
    useCallback(async (): Promise<boolean> => {
      const res = await updateMember({
        ...(careCircleMember as CareCircleMember),
        careCircleID: careCircleMember?.careCircleID || '',
        careStageID: careStage.value,
        personalization: JSON.parse(personalization.value).map((x: any) => ({
          id: x,
        })),
      });
      if (!('data' in res)) {
        console.error('Failed to update personalization', res.error);
        return false;
      }

      return true;
    }, [careStage, personalization, careCircleMember, updateMember]);

  const handleUpdateCareProfile = useCallback(async (): Promise<boolean> => {
    const res = await createManyAnswer(answers);
    if (!('data' in res)) {
      console.error('Failed to update care profile', res.error);
      return false;
    }

    return true;
  }, [answers, createManyAnswer]);

  // generate action plan
  const handleGenerateActionPlan = useCallback(async () => {
    const res = await generateActionPlan();
    if (!('data' in res)) {
      console.error('Failed to generate action plan', res.error);
      return false;
    }

    return true;
  }, [generateActionPlan]);

  const handleUpdateConsent = useCallback(async (): Promise<boolean> => {
    const res = await createManyAnswerConsent(answersConsent);
    if (!('data' in res)) {
      console.error('Failed to store consent', res.error);
      return false;
    }

    return true;
  }, [answersConsent, createManyAnswerConsent]);

  // memos
  const introConfig = useMemo(
    () => [
      {
        id: 'tos',
        Component: OnboardingTOS,
        onBeforeNext: handleUpdateConsent,
        config: [
          {
            id: '1',
            isIgnoreProgress: true,
            actions: [
              {
                label: 'I agree',
                onClick: OnboardingActionType.Next,
                isFullWidth: true,
              },
            ],
            consentForm: formConsent,
            value: answersConsent,
            setValue: setAnswersConsent,
          },
        ],
      },
    ],
    [handleUpdateConsent, formConsent, answersConsent, setAnswersConsent],
  );

  const typeformConfig = useMemo(
    () => [
      {
        id: 'typeform-onboarding',
        Component: OnboardingTypeform,
        config: [
          {
            id: '1',
            formID: options?.typeformID,
            isNativeApp: Capacitor.isNativePlatform(),
            onSubmit:
              (handleClick: (actionType: OnboardingActionType) => void) =>
              () => {
                Capacitor.isNativePlatform()
                  ? undefined
                  : handleClick(OnboardingActionType.Next);
              },
            actions: null,
          },
        ],
      },
    ],
    [options],
  );

  const onboardingV1Config = useMemo(
    () => [
      {
        id: 'what-stage-of-care',
        Component: OnboardingEditor,
        onBeforeNext: handleUpdateCareStage,
        isProgressBar: true,
        config: [
          {
            id: '1',
            title: `Where are you in your journey?`,
            value: careStage,
            onChange: setCareStage,
            actions: [
              {
                label: 'Continue',
                onClick: OnboardingActionType.Next,
                isFullWidth: true,
              },
            ],
            fieldDef: {
              field: {
                type: FieldType.RadioGroup,
                variant: 'box-fill',
                options: careStageOptions?.map((x) => ({
                  value: x.id,
                  label: x.label,
                })),
                size: 'md',
                maxColumns: 1,
                isCustom: false,
              },
              validation: validateCareStage,
            },
          },
        ],
      },
      {
        id: 'what-can-we-help-you-with',
        Component: OnboardingEditor,
        onBeforeNext: handleUpdatePersonalization,
        isProgressBar: true,
        config: [
          {
            id: '1',
            title: `What can we help you with?`,
            subTitle: 'Please select as many as you need',
            value: personalization,
            onChange: setPersonalization,
            actions: [
              {
                label: 'Continue',
                onClick: OnboardingActionType.Next,
                isFullWidth: true,
              },
            ],
            fieldDef: {
              field: {
                type: FieldType.MultiSelectBox,
                variant: 'box-fill',
                options: personalizationOptions?.map((x) => ({
                  value: x.id,
                  label: x.label,
                  icon: x.icon || 'star',
                  iconFill: x.iconFill || 'yellow',
                })),
                size: 'sm',
                maxColumns: 1,
                isCustom: false,
              },
              validation: validatePersonalization,
            },
          },
        ],
      },
    ],
    [
      handleUpdateCareStage,
      handleUpdatePersonalization,
      setCareStage,
      careStage,
      careStageOptions,
      setPersonalization,
      personalization,
      personalizationOptions,
    ],
  );

  const incompleteQuestionsConfig = useMemo(
    () => [
      {
        id: 'activated-start',
        Component: OnboardingActivatedStart,
        config: [
          {
            id: '1',
            title: 'Personalise Your Journey',
            text: [
              'Before scheduling a call with a care expert, we need to get to know you better.',
              'We’ll ask you a few questions about the loved one you want support for.',
              'Your answers will give you a bespoke action plan that solves your needs.',
            ],
            ctaButtonLabel: 'Personalise Now',
            actions: null,
            isIgnoreProgress: true,
          },
        ],
      },
    ],
    [],
  );

  const form = useMemo(() => forms?.[0], [forms]);

  const feelingConfig = useMemo(
    () => [
      {
        id: 'care-profile-questions',
        Component: OnboardingEditor,
        onBeforeNext: async () => {
          let res = await handleUpdateCareProfile();
          if (!res) {
            console.error('Failed to update care profile');
            return false;
          }

          res = await handleGenerateActionPlan();
          if (!res) {
            console.error('Failed to generate action plan');
            return false;
          }
          return true;
        },
        isProgressBar: true,
        config: [
          ...Object.keys(form?.fields || {}).map((id: any, i: number) => {
            const f = form?.fields?.[id];

            return {
              id: f.id,
              title: f.label,
              subTitle: f.supportText,
              tooltip: f.tooltip,
              value: { value: answers[f.id] },
              actions: [
                {
                  label: 'Continue',
                  onClick: OnboardingActionType.Next,
                  isFullWidth: true,
                },
              ],
              onChange: (value: any) => {
                setAnswers({
                  ...answers,
                  [f.id]: value.value,
                });
              },
              fieldDef: {
                ...f,
                isHideTitle: true,
                validation: ['care-recipient-medical-conditions'].includes(f.id)
                  ? () => true
                  : validateNotEmpty,
                isRequired: false,
              },
            };
          }),
        ],
      },
    ],
    [
      answers,
      handleGenerateActionPlan,
      handleUpdateCareProfile,
      form,
      setAnswers,
    ],
  );

  const loadingConfig = useMemo(
    () => [
      {
        id: 'loading',
        Component: OnboardingLoading,
        config: onboardingLoadingConfig,
        onStart: (handleClick: (action: OnboardingActionType) => void) => {
          setTimeout(
            () => {
              handleClick(OnboardingActionType.Next);
            },
            1400 + Math.random() * 300,
          );
        },
      },
    ],
    [],
  );

  return {
    introConfig,
    incompleteQuestionsConfig,
    loadingConfig,
    feelingConfig,
    updateIsOnboarded,
    personalization: parsedPersonalization,
    onboardingV1Config,
    typeformConfig,
  };
};
